import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { SevaProfileService } from 'src/app/Service/seva-profile.service';
import { CONSTANTS } from 'src/Core/Constant';

export interface DocumentUploadConfig {

  

  title: string;
  description: string;
  uploadButtonText: string;
  documentTypeLabel: string;
  acceptedFormats: string;
  validationErrorTitle: string;
  validationErrorMessage: string;
  documentType: number;
  maxFileSize: number; // in bytes
  allowedFormats: string[];
  endpoints: {
    upload: string;
    getAll: string;
    delete: string;
    saveParentAddendum: string; // added for parent save mode
  };
}

@Component({
  selector: 'app-addendum-document-upload',
  templateUrl: './addendum-document-upload.component.html',
  styleUrls: ['./addendum-document-upload.component.scss']
})
export class AddendumDocumentUploadComponent implements OnInit {
  @Input() config!: DocumentUploadConfig;
  @Input() showValidationError: boolean = false;
  @Input() isPreviewMode: boolean = false;
  @Output() documentsChange = new EventEmitter<any[]>();
  @Output() validationChange = new EventEmitter<boolean>();
  SaveMode = CONSTANTS.SAVE_MODE;
  documents: any[] = [];

  constructor(
    private api: SevaProfileService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {

  }

  ngOnChanges(): void {
      this.loadDocuments();
  }

  onFileSelected(event: Event): void {
    // Don't allow file selection in preview mode
    if (this.isPreviewMode) {
      return;
    }

    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }
    const file = input.files[0];

    // Show file size information
    this.showFileSizeInfo(file);

    // Validate file before upload
    if (this.validateFile(file)) {
      this.uploadDocument(file);
    }

    input.value = '';
  }

  private validateFile(file: File): boolean {
    // Check file size
    if (file.size > this.config.maxFileSize) {
      const maxSizeMB = this.config.maxFileSize / (1024 * 1024);
      this.toastr.error(`File size must be less than ${maxSizeMB}MB. Current size: ${this.formatFileSize(file.size)}`);
      return false;
    }

    // Check file format
    if (!this.config.allowedFormats.includes(file.type)) {
      this.toastr.error(`File must be in ${this.config.acceptedFormats} format only.`);
      return false;
    }

    return true;
  }

  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  private showFileSizeInfo(file: File): void {
    const fileSize = this.formatFileSize(file.size);
    const maxSize = this.formatFileSize(this.config.maxFileSize);

    if (file.size > this.config.maxFileSize) {
      this.toastr.warning(`File size: ${fileSize} (exceeds ${maxSize} limit)`);
    } else {
      this.toastr.info(`File size: ${fileSize} (within ${maxSize} limit)`);
    }
  }

  private uploadDocument(file: File): void {
    this.api
      .uploadDocument(this.config.documentType, file, this.config.endpoints.upload, {
        applicationTypeId: this.config.documentType
      })
      .subscribe({
        next: () => {
    
          this.saveParentAddendum({SubmissionMode: "file",SaveMode :  this.SaveMode.SAVE})
          this.loadDocuments();
        },
        error: (err) => {
          console.error('Upload failed', err);
          this.toastr.error('Failed to upload document. Please try again.');
        }
      });
  }

  private loadDocuments(): void {
    this.api
      .getAllDocuments(this.config.endpoints.getAll, { gspDocType: this.config.documentType })
      .subscribe({
        next: (res: any) => {
  
          
          this.documents = Array.isArray(res) ? res : (res?.Data?.UserDocuments ?? []);
          this.emitChanges();
        },
        error: (err) => console.error('Failed to load documents', err)
      });
  }

  hasDocument(): boolean {
    return this.documents && this.documents.length > 0;
  }

  isValid(): boolean {
    return this.hasDocument();
  }

  getDocumentByType(documentType: number): any {
    return this.documents.find((doc: any) => (doc.DocType ?? doc.documentType) === documentType);
  }

  deleteDocument(documentId: number, documentName: string): void {
    // Don't allow deletion in preview mode
    if (this.isPreviewMode) {
      return;
    }

    if (!documentId || documentId <= 0) {
      this.toastr.warning('No document to delete');
      return;
    }

    if (!documentName) {
      documentName = 'this document';
    }

    if (confirm(`Are you sure you want to delete "${documentName}"?`)) {
      this.api.apiCall(
        `${this.config.endpoints.delete}${documentId}`,
        'DELETE'
      ).subscribe({
        next: (response: any) => {
          this.saveParentAddendum({SubmissionMode: "file",SaveMode :  this.SaveMode.DRAFT})
          this.toastr.success('Document deleted successfully');
          this.loadDocuments();
        },
        error: (error: any) => {
          console.error('Delete failed:', error);
          this.toastr.error('Failed to delete document. Please try again.');
        }
      });
    }
  }

  downloadDocument(doc: any): void {
    if (!doc || !doc.Id) {
      this.toastr.warning('Document not found');
      return;
    }

    const link = document.createElement('a');
    link.href = `data:${doc.ContentType || 'application/octet-stream'};base64,${doc.FileContent || doc.fileContent}`;
    link.download = doc.FileName || doc.fileName || 'document';
    link.click();
  }

  private emitChanges(): void {
    this.documentsChange.emit(this.documents);
    this.validationChange.emit(this.isValid());
  }


  saveParentAddendum(reqBody: any): void {
    this.api.apiCall(this.config.endpoints.saveParentAddendum, 'POST', reqBody).subscribe((response: any) => {
      this.toastr.success(response?.Message);
    });
  }

}